<script setup lang="ts">
import type { Options as SplideOptions, Splide as SplideType } from '@splidejs/splide';
import { Splide } from '@splidejs/vue-splide';
import { useI18n } from 'vue-i18n';

import '@splidejs/vue-splide/css';

interface SliderProps {
  disableProgress?: boolean;
  sliderOptions?: SplideOptions;
}

const props = defineProps<SliderProps>();

const defaultSliderOptions: SplideOptions = {
  drag: true,
  keyboard: true,
  type: 'slide',
  pagination: false,
  mediaQuery: 'min',
  breakpoints: {
    0: {
      perPage: 1,
      arrows: false,
    },
    640: {},
    768: {},
    1280: {
      arrows: true,
    },
    1536: {
      perPage: 1,
    },
  },
  classes: {
    // Add classes for arrows.
    arrows: 'splide__arrows image-slider__arrows',
    arrow: 'splide__arrow image-slider__arrow',
    prev: 'splide__arrow--prev image-slider__prev',
    next: 'splide__arrow--next image-slider__next',

    // Add classes for pagination.
    pagination: 'splide__pagination image-slider__pagination', // container
    page: 'splide__pagination__page image-slider__page', // each button
  },
};

const effectiveSliderOptions = {
  ...defaultSliderOptions,
  ...props.sliderOptions,
};

const splide = ref<SplideType>();
const progressStyleWidth = ref<string>('');
const statusText = ref('');
const { t } = useI18n();

function announceSlide(instance: SplideType) {
  const slides = instance.Components.Elements.slides as HTMLElement[];
  const active = slides[instance.index];
  const total = slides.length;

  const heading = active?.querySelector('h1,h2,h3')?.textContent?.trim() ?? '';
  statusText.value = heading
    ? t('Aria.Global.Slider.NewSlideAddedWithHeading', {
        index: instance.index + 1,
        length: total,
        heroHeading: heading,
      })
    : t('Aria.Global.Slider.NewSlideAdded', { index: instance.index + 1, length: total });
}

function updateProgress(instance: SplideType) {
  const end = instance.Components.Controller.getEnd() + 1;
  const rate = Math.min((instance.index + 1) / end, 1);
  progressStyleWidth.value = `width:${String(100 * rate)}%`;
}

function handleSliderUpdate(instance: SplideType) {
  if (!props.disableProgress) {
    updateProgress(instance);
  }
  if (effectiveSliderOptions.live === false) {
    announceSlide(instance);
  }
}
</script>

<template>
  <div class="relative">
    <Splide
      ref="splide"
      :options="effectiveSliderOptions"
      @splide:mounted="handleSliderUpdate"
      @splide:move="handleSliderUpdate"
    >
      <slot></slot>
    </Splide>
    <p
      v-if="effectiveSliderOptions.live === false"
      class="sr-only"
      aria-live="polite"
      aria-atomic="true"
    >
      {{ statusText }}
    </p>
    <div
      v-if="!disableProgress"
      class="absolute bottom-0 z-10 w-full bg-black"
    >
      <div
        class="transition-width h-1 w-0 bg-primary duration-400 ease-in-out"
        :style="progressStyleWidth"
      ></div>
    </div>
  </div>
</template>
